/**
 * Ported from the lekterion codebase (`src/auth/jwt.ts`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 */
/**
 * Client-side JWT inspection (Phase 8). Decode-only — the browser NEVER trusts
 * these values for authorization; Convex re-validates the signature on every call
 * (see `convex/auth.config.ts`). We read claims here purely to drive the UI:
 * whether a stored token is still worth sending, the display nickname, and
 * whether to reveal admin affordances. The server enforces all of it.
 */

import {
  type SessionTyp,
  sessionTypOf,
  USER_SESSION_TYP,
} from "@bindery/shared/contracts/session-typ";

export interface LightAuthClaims {
  typ?: SessionTyp;
  sub?: string;
  exp?: number;
  readerNickname?: string;
  internalRole?: "founder" | "admin" | "support" | "researcher";
  email?: string;
  [key: string]: unknown;
}

/** Decode a JWT payload (no signature check). Returns null if malformed. */
export function decodeJwt(token: string): LightAuthClaims | null {
  const parts = token.split(".");
  const payload = parts[1];
  if (parts.length !== 3 || payload === undefined) return null;
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const parsed = JSON.parse(json) as unknown;
    // A JWT payload must be a JSON object; a primitive (number, string, null)
    // would pass JSON.parse but isn't a claims set — reject rather than cast it.
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed as LightAuthClaims;
  } catch {
    return null;
  }
}

/** A token is usable only while unexpired (with a small clock-skew margin). */
export function isExpired(
  claims: LightAuthClaims | null,
  skewSeconds = 30,
): boolean {
  if (!claims || typeof claims.exp !== "number") return true;
  return claims.exp * 1000 <= Date.now() + skewSeconds * 1000;
}

/** Admin = a WorkOS user_session with a privileged internal role. Display only. */
export function isAdminClaims(claims: LightAuthClaims | null): boolean {
  if (!claims || sessionTypOf(claims) !== USER_SESSION_TYP) return false;
  return claims.internalRole === "founder" || claims.internalRole === "admin";
}
